/**
 * apps/web/src/components/SubmittedFormsViewer.tsx
 * -------------------------------------------------------
 * Purpose:
 *   Render normalized submitted forms as a compact overview plus
 *   read-only section accordions.
 *
 * High-level data flow:
 *   - AuditViewerPage passes `detail.submittedForms` from the route loader.
 *   - This component renders one card per submitted form.
 *   - Each form shows:
 *       1) overview cards for key highlights
 *       2) accordion sections that preserve submitted field order
 */

import React from 'react';
import {
  Accordion,
  Badge,
  Card,
  Group,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from '@mantine/core';

import type { SubmittedForm, SubmittedFormField } from '@tinypro-audit/contracts';
import { formatDateOnly, formatDateTimeInAppTimeZone } from '@tinypro-audit/shared/time';

/** Return a human-friendly label for an attachment chip. */
function attachmentLabel(
  attachment: SubmittedFormField['attachments'][number],
  index: number,
): string {
  return attachment.originalFileName ?? attachment.fileName ?? `Attachment ${index + 1}`;
}

/** Format a normalized field into display text when it is not attachment-driven. */
function formatFieldValue(field: SubmittedFormField): string | undefined {
  if (field.type === 'Date') {
    return field.value ? formatDateOnly(field.value) : undefined;
  }

  if (field.type === 'Checkbox') {
    return field.values?.join(', ');
  }

  return field.value ?? field.values?.join(', ') ?? undefined;
}

/** Render a single submitted form field using safe type-based fallbacks. */
function FormFieldRenderer({ field }: { field: SubmittedFormField }): React.ReactElement {
  if (field.type === 'Picture') {
    if (field.attachments.length > 0) {
      return (
        <Group gap={6}>
          {field.attachments.map((attachment, index) => (
            <Badge key={`${field.id}.${index}`} variant="light">
              {attachmentLabel(attachment, index)}
            </Badge>
          ))}
        </Group>
      );
    }

    return field.value ? (
      <Text size="sm" style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
        {field.value}
      </Text>
    ) : (
      <Text size="sm" c="dimmed">
        No attachments uploaded.
      </Text>
    );
  }

  const value = formatFieldValue(field);

  if (value) {
    return (
      <Text size="sm" style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
        {value}
      </Text>
    );
  }

  if (field.attachments.length > 0) {
    return (
      <Group gap={6}>
        {field.attachments.map((attachment, index) => (
          <Badge key={`${field.id}.${index}`} variant="light">
            {attachmentLabel(attachment, index)}
          </Badge>
        ))}
      </Group>
    );
  }

  return (
    <Text size="sm" c="dimmed">
      No value provided.
    </Text>
  );
}

/** Render a compact overview cell for the submitted forms summary strip. */
function OverviewCard({ label, value }: { label: string; value: string }): React.ReactElement {
  return (
    <Card withBorder radius="md" p="sm">
      <Text size="xs" c="dimmed" tt="uppercase">
        {label}
      </Text>
      <Text fw={600} style={{ overflowWrap: 'anywhere' }}>
        {value}
      </Text>
    </Card>
  );
}

/** Render normalized submitted forms as overview cards plus section accordions. */
export function SubmittedFormsViewer({ forms }: { forms: SubmittedForm[] }): React.ReactElement {
  return (
    <Card withBorder radius="md">
      <Title order={4} mb="sm">
        Submitted Forms
      </Title>

      {forms.length === 0 ? (
        <Text c="dimmed" size="sm">
          No submitted forms available.
        </Text>
      ) : (
        <Stack gap="md">
          {forms.map((form) => (
            <Card key={form.id} withBorder radius="md" p="md">
              <Group justify="space-between" align="flex-start" mb="sm">
                <div>
                  <Text fw={700}>{form.formName}</Text>
                  {form.submittedAt ? (
                    <Text size="sm" c="dimmed">
                      Submitted {formatDateTimeInAppTimeZone(form.submittedAt)}
                    </Text>
                  ) : null}
                </div>
                {form.status ? <Badge variant="light">{form.status}</Badge> : null}
              </Group>

              {form.overview.length > 0 ? (
                <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} mb="md">
                  {form.overview.map((item) => (
                    <OverviewCard
                      key={`${form.id}.${item.label}`}
                      label={item.label}
                      value={item.value}
                    />
                  ))}
                </SimpleGrid>
              ) : null}

              {form.sections.length > 0 ? (
                <Accordion
                  multiple
                  variant="separated"
                  defaultValue={form.sections[0] ? [form.sections[0].id] : []}
                >
                  {form.sections.map((section) => (
                    <Accordion.Item key={section.id} value={section.id}>
                      <Accordion.Control>
                        <Group justify="space-between" wrap="nowrap">
                          <Text fw={600}>{section.title}</Text>
                          <Text size="xs" c="dimmed">
                            {section.fields.length} field{section.fields.length === 1 ? '' : 's'}
                          </Text>
                        </Group>
                      </Accordion.Control>
                      <Accordion.Panel>
                        <Stack gap="sm">
                          {section.fields.map((field) => (
                            <div key={field.id}>
                              <Text
                                size="sm"
                                fw={600}
                                mb={4}
                                style={{ overflowWrap: 'anywhere' }}
                              >
                                {field.label}
                              </Text>
                              <FormFieldRenderer field={field} />
                            </div>
                          ))}
                        </Stack>
                      </Accordion.Panel>
                    </Accordion.Item>
                  ))}
                </Accordion>
              ) : (
                <Text c="dimmed" size="sm">
                  No submitted sections available.
                </Text>
              )}
            </Card>
          ))}
        </Stack>
      )}
    </Card>
  );
}
